import {
    IBaseGameConfig,
    IMapQueryConfig,
    IMapQueryItem,
} from "../gameLoader/types/IGameLoader.types";
import { queryFunctions } from "../gameLoader/loaders/queryFunctions";

const getElementAttribute = <T = string>(
    item: Element,
    key: string,
    _default: T
): T => {
    if (!(item instanceof Element)) {
        return _default;
    }

    if (!item.hasAttribute(key)) {
        return _default;
    }

    return String(item.getAttribute(key)).trim() as T;
};

const getElementAttributesByPrefix = (
    item: Element,
    prefix: string
): { [index: string]: string } | undefined => {
    try {
        return Array.from(item.attributes)
            .filter((attr) => attr.name.startsWith(prefix))
            .reduce(
                (object, attr) => ({
                    ...object,
                    [attr.name.trim()]: attr.value.trim(),
                }),
                {}
            );
    } catch (e) {
        console.warn(
            `Function attributes by prefix: "${prefix}" not found! Error:`,
            e,
            item
        );
        return undefined;
    }
};

export const parseMapQueryParams = (document: Document): IMapQueryConfig => {
    if (!document) {
        return {};
    }

    const queryItemsDocuments = Array.from(
        document.querySelectorAll("config > mapQuery > query")
    );

    return queryItemsDocuments.reduce(
        (object: { [index: string]: IMapQueryItem }, item: Element) => {
            const key = getElementAttribute<string>(item, "key", "");
            const type = getElementAttribute<
                "add" | "rename" | "delete" | "function"
            >(item, "type", "add");
            const as = getElementAttribute<string>(item, "as", "");
            const value = getElementAttribute<string>(item, "value", "");
            const functionName = getElementAttribute<string>(
                item,
                "function",
                ""
            );
            const force =
                getElementAttribute<string>(item, "force", "") === "force";

            if (!type || !key) {
                return object;
            }

            if (!["add", "rename", "delete", "function"].includes(type)) {
                console?.warn(
                    `[GM] Type "${type}" not found in ['add', 'rename', 'delete', 'function']`
                );
                return object;
            }

            const args =
                type === "function"
                    ? getElementAttributesByPrefix(item, "arg_")
                    : undefined;

            return {
                ...object,
                [key]: {
                    type,
                    as,
                    value,
                    force,
                    function: functionName,
                    args,
                },
            };
        },
        {}
    );
};

const executeQueryFunction = (
    item: IMapQueryItem | undefined,
    params: Partial<IBaseGameConfig>,
    key: string,
    mutableParams?: Partial<IBaseGameConfig>,
    localParams?: Partial<IBaseGameConfig>,
): string | undefined => {
    if (!item?.function || !queryFunctions.hasOwnProperty(item?.function)) {
        console.warn(`Query function "${item?.function}" not found!`);
        return undefined;
    }

    try {
        return queryFunctions[item?.function](item.args, params, key, mutableParams, localParams);
    } catch (e) {
        console.warn(`Query function "${item?.function}" error: `, e);
        return undefined;
    }
};

export const executeMapQueryParams = (
    params: Partial<IBaseGameConfig>,
    config: Partial<IMapQueryConfig>,
    newParams: Partial<IBaseGameConfig> = { ...(params || {}) }
): Partial<IBaseGameConfig> => {
    const keys = Object.keys(config);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const {
            type = "add",
            as = "",
            value = "",
            force = false,
        } = config[key] as IMapQueryItem;

        const variableValue: string = (() => {
            if (!!(params as any)[key] && !force) {
                return (params as any)[key];
            }

            return value;
        })();

        const newKey = !!as ? as : key;

        if (["rename", "add", "function"].includes(type)) {
            if (!variableValue) {
                continue;
            }

            if (!!as && as !== key && newParams.hasOwnProperty(key)) {
                delete (newParams as any)[key];
            }

            (newParams as any)[newKey] = variableValue;
        }

        if (type === "delete" && newParams?.hasOwnProperty(key)) {
            delete (newParams as any)[key];
        }
    }

    return newParams;
};

export const executeFunctionMapQueryParams = (
    params: Partial<IBaseGameConfig>,
    config: Partial<IMapQueryConfig>,
    localParams: Partial<IBaseGameConfig>,
    newParams: Partial<IBaseGameConfig> = { ...(params || {}) },
): Partial<IBaseGameConfig> => {
    const keys = Object.keys(config);

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const { type = "add", as = "", force } = config[key] as IMapQueryItem;

        if (type !== "function") {
            continue;
        }

        const newKey = !!as ? as : key;

        const fnResult = executeQueryFunction(config[key], params, key, newParams, localParams);
        if (fnResult !== undefined) {
            if (newParams.hasOwnProperty(key) && !force) {
                continue;
            }

            if (!!as && as !== key && newParams.hasOwnProperty(key)) {
                delete (newParams as any)[key];
            }

            (newParams as any)[newKey] = fnResult;
        }
    }

    return newParams;
};
